import React, { useState, useCallback } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Button } from '../../../components/ui/button';
import { Loader2, BellRing, Mail, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAbortableLoad, isCanceled } from '../../../hooks/useAbortableLoad';

const SWITCHES = [
  { key: 'notif_nuevo', label: 'Nuevo Ticket', desc: 'Al registrarse un ticket, se notifica a todos los usuarios de áreas que pueden responder al cliente.' },
  { key: 'notif_asignar', label: 'Asignar Responsable', desc: 'Al asignar un responsable, se le envía el correo con los datos del ticket y el plazo (SLA).' },
  { key: 'notif_sin_gestion', label: 'Ticket sin gestión', desc: 'Cuando un ticket permanece en estado "Nuevo" N días hábiles, se notifica a las áreas que pueden responder al cliente.' },
  { key: 'notif_proximo_vencer', label: 'Ticket próximo a vencer', desc: 'Cuando faltan N días hábiles para vencer el plazo: responsable, áreas que responden al cliente y responsables de gestión interna abierta.' },
  { key: 'notif_vencido', label: 'Ticket vencido', desc: 'Al vencer el plazo: responsable, áreas que responden al cliente y responsables de gestión interna abierta.' },
  { key: 'notif_terminado', label: 'Ticket terminado', desc: 'Al terminar el ticket, se notifica al responsable y a las áreas que pueden responder al cliente.' },
  { key: 'notif_reabierto', label: 'Ticket reabierto', desc: 'Cuando el cliente responde y el ticket se reabre automáticamente, se notifica al responsable y a los responsables de gestión interna abierta.' },
  { key: 'notif_respuesta_cliente', label: 'Cliente respondió (ticket abierto)', desc: 'Cuando el cliente responde a un ticket que sigue abierto (no reapertura), se notifica al responsable principal.' },
  { key: 'notif_gestion_interna_respuesta', label: 'Notificar respuesta de gestión interna', desc: 'Notifica por correo al destinatario: resolutor interno o responsable principal.' },
  { key: 'notif_acuse_cliente', label: 'Acuse de recibo al cliente', desc: 'Al crear el ticket, se envía al cliente un correo de acuse de recibo con el código del ticket.' },
  { key: 'notif_cierre_cliente', label: 'Correo de cierre al cliente', desc: 'Al terminar el ticket automáticamente (sin respuesta escrita del agente), se envía al cliente el correo de cierre.' },
];

const NUM_FIELDS = [
  { key: 'plazo_max_dias_habiles', label: 'Plazo máximo de atención (días hábiles)', help: 'Plazo legal en días hábiles para dar solución al ticket.' },
  { key: 'dias_sin_gestion', label: 'Días hábiles para "sin gestión"', help: 'Días hábiles en estado "Nuevo" antes de alertar por falta de gestión.' },
  { key: 'dias_previo_vencimiento', label: 'Días hábiles de anticipación ("próximo a vencer")', help: 'Con cuántos días hábiles de anticipación avisar antes del vencimiento.' },
];

export default function NotificacionesTab({ api, isAdmin }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [muestraEmail, setMuestraEmail] = useState('');
  const [cfg, setCfg] = useState(null);

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const res = await api.get('/tickets/config-notificaciones', { signal });
      setCfg(res.data);
    } catch (e) {
      if (isCanceled(e)) return;
      toast.error('No se pudo cargar la configuración de notificaciones');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useAbortableLoad((signal) => load(signal), [load]);

  const setField = (key, value) => setCfg((prev) => ({ ...prev, [key]: value }));

  const guardar = async () => {
    setSaving(true);
    try {
      const payload = {
        plazo_max_dias_habiles: Number(cfg.plazo_max_dias_habiles),
        dias_sin_gestion: Number(cfg.dias_sin_gestion),
        dias_previo_vencimiento: Number(cfg.dias_previo_vencimiento),
        notif_nuevo: !!cfg.notif_nuevo,
        notif_asignar: !!cfg.notif_asignar,
        notif_sin_gestion: !!cfg.notif_sin_gestion,
        notif_proximo_vencer: !!cfg.notif_proximo_vencer,
        notif_vencido: !!cfg.notif_vencido,
        notif_terminado: !!cfg.notif_terminado,
        notif_reabierto: !!cfg.notif_reabierto,
        notif_respuesta_cliente: !!cfg.notif_respuesta_cliente,
        notif_gestion_interna_respuesta: !!cfg.notif_gestion_interna_respuesta,
        notif_acuse_cliente: !!cfg.notif_acuse_cliente,
        notif_cierre_cliente: !!cfg.notif_cierre_cliente,
      };
      const res = await api.put('/tickets/config-notificaciones', payload);
      setCfg(res.data);
      toast.success('Configuración de notificaciones guardada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const enviarMuestras = async () => {
    if (!muestraEmail) { toast.error('Ingresa un correo para el envío de ejemplos'); return; }
    setEnviando(true);
    try {
      const res = await api.post('/tickets/config-notificaciones/enviar-muestras', { to: muestraEmail });
      toast.success(`Se enviaron ${res.data.enviados}/${res.data.total} ejemplos a ${res.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar ejemplos');
    } finally {
      setEnviando(false);
    }
  };

  if (loading || !cfg) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="flex flex-col gap-6" data-testid="notificaciones-tab">
      <div className="flex items-center gap-2">
        <BellRing className="w-5 h-5 text-slate-700" />
        <div>
          <h3 className="font-semibold text-slate-900">Notificaciones por correo</h3>
          <p className="text-sm text-slate-500">Configura los avisos internos sobre los hitos de cada ticket.</p>
        </div>
      </div>

      {/* Parámetros numéricos */}
      <div className="grid gap-4 sm:grid-cols-3">
        {NUM_FIELDS.map((f) => (
          <div key={f.key} className="flex flex-col gap-2">
            <Label htmlFor={f.key} className="text-sm">{f.label}</Label>
            <Input
              id={f.key}
              type="number"
              min={1}
              data-testid={`notif-${f.key}`}
              value={cfg[f.key] ?? ''}
              disabled={!isAdmin}
              onChange={(e) => setField(f.key, e.target.value)}
            />
            <p className="text-xs text-slate-400">{f.help}</p>
          </div>
        ))}
      </div>

      {/* Nota de frecuencia */}
      <div className="flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600" data-testid="notif-frecuencia-nota">
        <Clock className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" />
        <span>Las alertas de <strong>sin gestión</strong>, <strong>próximo a vencer</strong> y <strong>vencido</strong> se evalúan y envían automáticamente <strong>una vez al día, a las 08:00 (hora Lima), de lunes a viernes</strong>. Los días hábiles excluyen fines de semana y feriados nacionales de Perú.</span>
      </div>

      {/* Switches */}
      <div className="flex flex-col divide-y divide-slate-100 rounded-lg border border-slate-200">
        {SWITCHES.map((s) => (
          <div key={s.key} className="flex items-center justify-between gap-4 p-4">
            <div className="pr-2">
              <Label htmlFor={s.key} className="text-sm font-medium cursor-pointer">{s.label}</Label>
              <p className="text-xs text-slate-500 mt-0.5">{s.desc}</p>
            </div>
            <Switch
              id={s.key}
              data-testid={`notif-switch-${s.key}`}
              checked={!!cfg[s.key]}
              disabled={!isAdmin}
              onCheckedChange={(v) => setField(s.key, v)}
            />
          </div>
        ))}
      </div>

      {isAdmin && (
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <Button onClick={guardar} disabled={saving} data-testid="notif-guardar-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Guardar configuración
          </Button>
          <div className="flex-1" />
          <div className="flex items-end gap-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="muestra-email" className="text-xs text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" /> Enviar ejemplos a</Label>
              <Input
                id="muestra-email"
                type="email"
                placeholder="correo@empresa.pe"
                data-testid="notif-muestra-email"
                value={muestraEmail}
                onChange={(e) => setMuestraEmail(e.target.value)}
                className="w-56"
              />
            </div>
            <Button variant="outline" onClick={enviarMuestras} disabled={enviando} data-testid="notif-enviar-muestras-btn">
              {enviando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar ejemplos
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
